import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { estimationsApi } from '../api/estimations';
import useAuthStore from '../store/authStore';

export default function Dashboard() {
  const { user, isAdminOrManager } = useAuthStore();
  const [stats, setStats] = useState({ total: 0, recent: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await estimationsApi.list({ page: 1, page_size: 5 });
      setStats({
        total: response.data.total,
        recent: response.data.estimations,
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const complexityColor = (c) => {
    const colors = { LOW: 'bg-green-100 text-green-700', MEDIUM: 'bg-yellow-100 text-yellow-700', HIGH: 'bg-red-100 text-red-700' };
    return colors[c] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Bienvenido, {user?.full_name || user?.username}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Panel de control — IT Aduax Estimación de Tiempos
          </p>
        </div>
        <Link
          to="/estimations/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          + Nueva Estimación
        </Link>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <p className="text-sm text-gray-500">Total estimaciones</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <p className="text-sm text-gray-500">Activas</p>
          <p className="text-3xl font-bold text-blue-600 mt-1">
            {stats.recent.filter((e) => e.status === 'Activo').length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <p className="text-sm text-gray-500">Rol</p>
          <p className="text-3xl font-bold text-purple-600 mt-1">
            {user?.roles?.[0] || 'USER'}
          </p>
        </div>
      </div>

      {/* Recent estimations */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-semibold text-gray-800">Estimaciones recientes</h2>
          <Link to="/estimations" className="text-sm text-blue-600 hover:text-blue-700">
            Ver todas →
          </Link>
        </div>
        <div className="divide-y divide-gray-100">
          {stats.recent.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <p>No hay estimaciones todavía.</p>
              <Link to="/estimations/new" className="text-blue-600 hover:underline mt-2 inline-block">
                Crear tu primera estimación
              </Link>
            </div>
          ) : (
            stats.recent.map((est) => (
              <Link
                key={est.id}
                to={`/estimations/${est.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{est.project_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {est.owner_username} · {new Date(est.created_at).toLocaleDateString('es-MX')}
                    {est.shared_with_me && (
                      <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                        Compartido ({est.shared_with_me})
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${complexityColor(est.complexity)}`}>
                    {est.complexity}
                  </span>
                  <span className="text-sm font-medium text-gray-700">{est.total_hours}h</span>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
